import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import fleetApi, { fleetError } from "@/lib/fleetApi";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, MailCheck } from "lucide-react";

const field = "mt-1.5 border-slate-700 bg-slate-900 text-white placeholder:text-slate-500 focus-visible:ring-blue-600";

const F = ({ id, label, ...props }) => (
  <div>
    <Label htmlFor={id} className="text-slate-300">{label}</Label>
    <Input id={id} data-testid={`signup-${id}`} className={field} {...props} />
  </div>
);

export default function SignupPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState("details");
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");
  const [f, setF] = useState({ org_name: "", full_name: "", email: "", phone: "", password: "", confirm: "", fleet_size: "", state: "", business_address: "" });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const start = async (e) => {
    e.preventDefault();
    if (f.password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (f.password !== f.confirm) { toast.error("Passwords don't match."); return; }
    setBusy(true);
    try {
      const { confirm, ...body } = f;
      await fleetApi.post("/auth/signup/start", { ...body, email: body.email.trim().toLowerCase(), fleet_size: Number(body.fleet_size) || 0 });
      setStep("verify");
      toast.success(`We sent a verification code to ${f.email}.`);
    } catch (err) {
      toast.error(fleetError(err, "Couldn't start signup."));
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await fleetApi.post("/auth/signup/verify", { email: f.email.trim().toLowerCase(), code: code.trim() });
      const res = await login(f.email.trim().toLowerCase(), f.password);
      if (!res.ok) { toast.error(res.error); navigate("/login"); return; }
      api.post("/onboarding/welcome").catch(() => {});
      toast.success(`Welcome to RideStamp, ${f.full_name.split(" ")[0]}!`);
      navigate("/portal/dashboard", { replace: true });
    } catch (err) {
      toast.error(fleetError(err, "That code didn't work."));
    } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen items-start justify-center bg-slate-950 p-6 lg:items-center" data-testid="signup-page">
      <div className="w-full max-w-xl py-8">
        <button onClick={() => navigate("/login")} className="mb-6 flex items-center gap-2 text-sm font-medium text-slate-400 hover:text-white" data-testid="signup-back">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </button>
        <Logo variant="light" />
        {step === "details" ? (
          <>
            <h1 className="mt-6 font-display text-3xl font-bold text-white">Create your business account</h1>
            <p className="mt-2 text-slate-400">One login for the web portal and the RideStamp app. Every plan starts with a 2-month free trial.</p>
            <form onSubmit={start} className="mt-8 grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2"><F id="org_name" label="Business name" value={f.org_name} onChange={set("org_name")} required placeholder="MetroCare Transportation" /></div>
              <F id="full_name" label="Your full name" value={f.full_name} onChange={set("full_name")} required placeholder="Alex Carter" />
              <F id="phone" label="Phone" type="tel" value={f.phone} onChange={set("phone")} placeholder="(617) 555-0123" />
              <div className="sm:col-span-2"><F id="email" label="Work email" type="email" value={f.email} onChange={set("email")} required placeholder="you@company.com" /></div>
              <F id="password" label="Password" type="password" value={f.password} onChange={set("password")} required placeholder="At least 8 characters" />
              <F id="confirm" label="Confirm password" type="password" value={f.confirm} onChange={set("confirm")} required />
              <div className="sm:col-span-2"><F id="business_address" label="Business address" value={f.business_address} onChange={set("business_address")} placeholder="Street, city, state ZIP — sets your time zone" /></div>
              <F id="state" label="State" value={f.state} onChange={set("state")} placeholder="MA" maxLength={2} />
              <F id="fleet_size" label="Number of vehicles" type="number" min="0" value={f.fleet_size} onChange={set("fleet_size")} placeholder="12" />
              <Button type="submit" disabled={busy} data-testid="signup-submit" className="mt-2 w-full gap-2 rounded-lg bg-blue-600 py-6 text-base font-semibold text-white hover:bg-blue-500 sm:col-span-2">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : null} Create business account
              </Button>
              <p className="text-center text-xs text-slate-500 sm:col-span-2">
                By creating an account you agree to the <Link to="/terms" className="text-blue-400 hover:underline">Terms</Link> and <Link to="/privacy" className="text-blue-400 hover:underline">Privacy Policy</Link>.
              </p>
            </form>
          </>
        ) : (
          <>
            <div className="mt-6 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-500/10 text-blue-400"><MailCheck className="h-6 w-6" /></div>
            <h1 className="mt-4 font-display text-3xl font-bold text-white">Check your email</h1>
            <p className="mt-2 text-slate-400">Enter the verification code we sent to <span className="font-semibold text-white">{f.email}</span>.</p>
            <form onSubmit={verify} className="mt-8 space-y-5">
              <div>
                <Label htmlFor="code" className="text-slate-300">Verification code</Label>
                <Input id="code" value={code} onChange={(e) => setCode(e.target.value)} required autoFocus inputMode="numeric"
                  placeholder="123456" data-testid="signup-code" className={`${field} text-center font-mono-data text-xl tracking-[0.4em]`} />
              </div>
              <Button type="submit" disabled={busy || !code.trim()} data-testid="signup-verify" className="w-full gap-2 rounded-lg bg-blue-600 py-6 text-base font-semibold text-white hover:bg-blue-500">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : null} Verify & open my portal
              </Button>
              <div className="flex justify-between text-sm">
                <button type="button" onClick={() => setStep("details")} className="text-slate-400 hover:text-white" data-testid="signup-edit">Edit details</button>
                <button type="button" onClick={start} disabled={busy} className="text-blue-400 hover:underline" data-testid="signup-resend">Resend code</button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
